/**
 * useLivePrice
 *
 * Live price for a token from our price service (prices.trigger.fun). The app
 * never connects to exchanges for prices.
 *
 * - The venues listing the token come from `/v1/markets` (cached 30 min, or
 *   60 s while one has no price yet); a venue with a price is preferred. A
 *   failed lookup is retried (backing off to once a minute) while mounted.
 * - Prices stream over one shared WebSocket for the whole app (see
 *   `lib/prices/price-stream.ts`); a new market gets a REST snapshot first,
 *   and the last price is kept so navigating back shows it immediately.
 *
 * Exchange selection (`chooseLivePriceMarket`):
 *   - Futures first: `exchange` when it lists the token, else the first
 *     futures venue in priority order (binance, bybit, gate, kucoin,
 *     hyperliquid, aster).
 *   - When `futuresOnly` is false and no futures venue lists the token, the
 *     spot venue (binance, bybit, gate, kucoin).
 *   - If the service has no price for the chosen market, there is no price.
 *
 * @param symbol       Token symbol, e.g. "BTC", "ETHUSDT"
 * @param exchange     Optional preferred exchange
 * @param futuresOnly  When true (default), never use a spot price.
 */

import { useEffect, useReducer } from "react";

import {
  chooseLivePriceMarket,
  getPriceMarkets,
  getPriceStream,
  peekPriceMarkets,
  toServiceSymbol,
  type PriceUpdate,
  type ServiceMarket,
} from "@/lib/prices";
import type { TokenAvailability } from "@/lib/market-data/market-data.types";

export type { PriceUpdate } from "@/lib/prices";

export interface UseLivePriceResult {
  /** Latest price from the price service. */
  price: PriceUpdate | null;
  /** The market the price comes from, once chosen. */
  market: ServiceMarket | null;
  /** Whether the token has a market to price (futures, or spot when allowed). */
  availability: TokenAvailability | null;
  /** True while the listing venues are being looked up. */
  isLoading: boolean;
  /**
   * True once a market is chosen but no price has arrived yet.
   */
  isConnecting: boolean;
  /** Human-readable error message, or null if everything is healthy. */
  error: string | null;
}

interface LivePriceState {
  price: PriceUpdate | null;
  market: ServiceMarket | null;
  availability: TokenAvailability | null;
  isLoading: boolean;
  error: string | null;
}

type LivePriceAction =
  | { type: "RESET" }
  | { type: "LOADING" }
  | { type: "MARKET"; payload: { market: ServiceMarket; symbol: string } }
  | { type: "NOT_AVAILABLE"; payload: string }
  | { type: "PRICE"; payload: PriceUpdate }
  | { type: "ERROR"; payload: string };

const INITIAL_STATE: LivePriceState = {
  price: null,
  market: null,
  availability: null,
  isLoading: false,
  error: null,
};

/**
 * Retry a failed venue lookup (e.g. before sign-in finished) after these
 * delays, then every LOOKUP_RETRY_MAX_MS for as long as the hook is mounted.
 */
const LOOKUP_RETRY_MS = [3_000, 10_000, 30_000];
const LOOKUP_RETRY_MAX_MS = 60_000;

function reducer(
  state: LivePriceState,
  action: LivePriceAction
): LivePriceState {
  switch (action.type) {
    case "RESET":
      return INITIAL_STATE;
    case "LOADING":
      return { ...INITIAL_STATE, isLoading: true };
    case "MARKET": {
      const { market, symbol } = action.payload;
      const sameMarket =
        state.market?.exchange === market.exchange &&
        state.market?.symbol === market.symbol;
      return {
        price: sameMarket ? state.price : null,
        market,
        availability: {
          symbol,
          available: market.market === "futures",
          spotAvailable: market.market === "spot",
          status: "TRADING",
        },
        isLoading: false,
        error: null,
      };
    }
    case "NOT_AVAILABLE":
      return {
        price: null,
        market: null,
        availability: {
          symbol: action.payload,
          available: false,
          status: "NOT_FOUND",
        },
        isLoading: false,
        error: null,
      };
    case "PRICE":
      return { ...state, price: action.payload, error: null };
    case "ERROR":
      return { ...state, error: action.payload, isLoading: false };
  }
}

export function useLivePrice(
  symbol: string,
  exchange?: string,
  futuresOnly = true,
  enabled = true
): UseLivePriceResult {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);

  useEffect(() => {
    if (!enabled || !symbol) {
      dispatch({ type: "RESET" });
      return;
    }

    const symbolNorm = toServiceSymbol(symbol);
    if (!symbolNorm) {
      dispatch({ type: "RESET" });
      return;
    }

    let cancelled = false;
    let unsubscribe: (() => void) | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let attempt = 0;

    const applyMarkets = (markets: ServiceMarket[]) => {
      const market = chooseLivePriceMarket(markets, {
        requested: exchange,
        allowSpot: !futuresOnly,
      });
      if (!market) {
        dispatch({ type: "NOT_AVAILABLE", payload: symbolNorm });
        return;
      }
      dispatch({ type: "MARKET", payload: { market, symbol: symbolNorm } });
      unsubscribe = getPriceStream().subscribePrice(
        market.exchange,
        market.symbol,
        {
          onPrice: (update) => {
            if (!cancelled) dispatch({ type: "PRICE", payload: update });
          },
          onListed: (listed) => {
            if (!cancelled && !listed) {
              dispatch({ type: "NOT_AVAILABLE", payload: symbolNorm });
            }
          },
        }
      );
    };

    const lookup = () => {
      getPriceMarkets(symbolNorm)
        .then((markets) => {
          if (!cancelled) applyMarkets(markets);
        })
        .catch((err: unknown) => {
          if (cancelled) return;
          dispatch({
            type: "ERROR",
            payload:
              err instanceof Error ? err.message : "Price lookup failed",
          });
          // Keeps trying; the next successful lookup clears the error.
          const delay = LOOKUP_RETRY_MS[attempt] ?? LOOKUP_RETRY_MAX_MS;
          attempt += 1;
          retryTimer = setTimeout(lookup, delay);
        });
    };

    // Cached venues: pick the market and show the last price in this render
    // pass instead of flashing a loading state.
    const cached = peekPriceMarkets(symbolNorm);
    if (cached) {
      applyMarkets(cached);
    } else {
      dispatch({ type: "LOADING" });
      lookup();
    }

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      unsubscribe?.();
    };
  }, [symbol, exchange, futuresOnly, enabled]);

  const isConnecting =
    !state.isLoading &&
    state.market !== null &&
    state.price === null &&
    state.error === null;

  return {
    price: state.price,
    market: state.market,
    availability: state.availability,
    isLoading: state.isLoading,
    isConnecting,
    error: state.error,
  };
}
